import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link } from "react-router";
import {
  ConnectorRunsPageSchema,
  SyncHealthResponseSchema,
  type ConnectorHealthSummary,
  type SyncHealthResponse,
} from "@hyphesync/shared";
import { ApiError, describeError, useApi } from "../lib/api";
import { resume as resumeAction, runNow as runNowAction } from "../lib/connectorActions";
import { btn, btnPrimary, fmtDateTimeSec as fmtTime } from "../lib/ui";
import {
  ERROR_CLASS_TEXT,
  FAST_POLL_FOR_MS,
  HEALTH_TEXT,
  PROVIDER_LABEL,
  RUN_STATUS_TEXT,
  afterAction,
  cardErrorSawUpdate,
  createLatestGate,
  errorStillApplies,
  newCardError,
  type CardError,
  loadOlderWithRetry,
  applyOlderPage,
  applyPoll,
  bytesText,
  cadenceLabel,
  countsText,
  durationText,
  nextRunText,
  relativeTime,
  toneClass,
  type RunHistory,
} from "../lib/connectors";
import { isAdmin } from "../lib/roles";
import { useVisiblePoll } from "../lib/usePoll";
import { useMe } from "../lib/session";

const COLLAPSED_RUNS = 5;

/** Sync health (plan §8 row 3, OD-33, OD-34): admin + manager. Everything shown is computed by the
 *  API; this page polls it every 60 s while visible (OD-15) and formats. Admins also get Run now
 *  and Resume here; managers see the same, read-only. */
export function SyncHealth() {
  const api = useApi();
  const me = useMe();
  const admin = isAdmin(me);
  const [data, setData] = useState<SyncHealthResponse | null>(null);
  const [history, setHistory] = useState<Record<string, RunHistory>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fastUntil, setFastUntil] = useState(0);
  const [gate] = useState(createLatestGate); // lazy: built once, not on every render
  // The latest RENDERED history, for "Load older" to pick its cursor and decide whether to re-ask.
  // The merge itself always happens inside setHistory's updater, against React's latest state,
  // so it can never overwrite a poll that landed but hasn't rendered yet.
  const historyRef = useRef(history);
  historyRef.current = history;

  const load = useCallback(async () => {
    const mine = gate.begin(); // only the newest answer is applied
    setLoading(true);
    try {
      const res = SyncHealthResponseSchema.parse(await api("GET", "/api/sync-health"));
      if (!gate.isCurrent(mine)) return;
      setData(res);
      setHistory((prev) => Object.fromEntries(res.connectors.map((c) => [c.id, applyPoll(prev[c.id], c.runs, c.olderRunsCursor)])));
      setLoadError(null);
    } catch (e) {
      if (gate.isCurrent(mine)) setLoadError(describeError(e));
    } finally {
      if (gate.isCurrent(mine)) setLoading(false);
    }
  }, [api, gate]);

  // Mount load, then poll while the tab is visible; refresh at once on coming back to it.
  useVisiblePoll(load, fastUntil);

  // If a poll moved the card while the request was in flight, the page no longer connects:
  // ask again from the card's CURRENT cursor (a bounded number of times), never drop it silently.
  const loadOlder = (id: string): Promise<boolean> =>
    loadOlderWithRetry({
      cursorNow: () => historyRef.current[id]?.olderCursor ?? null,
      fetchPage: async (cursor) =>
        ConnectorRunsPageSchema.parse(await api("GET", `/api/sync-health/${id}/runs?before=${encodeURIComponent(cursor)}`)),
      // Merge against React's LATEST state (a poll may be queued but not rendered), committed at
      // once (flushSync) so the retry knows whether it applied: if the card moved, ask again.
      apply: (cursor, page) => {
        let applied = false;
        flushSync(() => {
          setHistory((prev) => {
            const h = prev[id];
            const merged = h ? applyOlderPage(h, cursor, page.runs, page.olderRunsCursor) : null;
            applied = merged !== null;
            return merged ? { ...prev, [id]: merged } : prev;
          });
        });
        return applied;
      },
    });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Sync health</h1>
          <p className="mt-1 text-muted">
            Every connector's latest runs. Refreshes every minute while this tab is open.
            {data && <> Updated {fmtTime(data.generatedAt)}.</>}
          </p>
        </div>
        <button type="button" disabled={loading} onClick={() => void load()} className={btn}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>

      {loadError && (
        <p role="alert" className="rounded-lg border border-line bg-card p-4 text-bad">
          {loadError}{" "}
          <button type="button" onClick={() => void load()} className="touch-target font-medium text-brand underline">Retry</button>
        </p>
      )}

      {data?.database && (
        <p className="text-sm text-muted">
          Database (all workspaces, super admins only): {bytesText(data.database.sizeBytes)}. A workspace moves to its own database past 5 GB (plan §6).
        </p>
      )}

      {data === null && !loadError && <p className="text-muted">Loading…</p>}
      {data?.connectors.length === 0 && (
        <p className="text-muted">
          No connectors yet.{admin && <> Add one in <Link to="/settings/connectors" className="font-medium text-brand underline">Settings → Connectors</Link>.</>}
        </p>
      )}

      {data?.connectors.map((c) => (
        <ConnectorHealthCard
          key={c.id}
          c={c}
          now={data.generatedAt}
          history={history[c.id]}
          admin={admin}
          onLoadOlder={() => loadOlder(c.id)}
          // Re-running the poll effect refreshes at once, then every 15 s for a while.
          onActed={() => setFastUntil(Date.now() + FAST_POLL_FOR_MS)}
          onRefused={() => void load()}
        />
      ))}
    </div>
  );
}

function ConnectorHealthCard({ c, now, history, admin, onLoadOlder, onActed, onRefused }: {
  c: ConnectorHealthSummary;
  now: string;
  history: RunHistory | undefined;
  admin: boolean;
  /** False when new runs kept moving the card and the older page never connected. */
  onLoadOlder: () => Promise<boolean>;
  onActed: () => void;
  /** An action was refused: refresh once (the card may be stale), no fast polling. */
  onRefused: () => void;
}) {
  const api = useApi();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // Same lifetime as Settings → Connectors (lib/connectors.ts errorStillApplies): pinned to the
  // first state a refresh shows, gone once that state changes or, for a moment refusal, at the
  // next refresh. Each poll gives this card a new `c` object.
  const [error, setError] = useState<CardError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    setError((cur) => (cur ? cardErrorSawUpdate(cur, c) : cur));
  }, [c]);
  const shownError = error && errorStillApplies(error, c) ? error.text : null;
  const health = HEALTH_TEXT[c.health];
  const runs = history?.runs ?? c.runs;
  const shown = expanded ? runs : runs.slice(0, COLLAPSED_RUNS);

  /** Run an action that confirms itself (lib/connectorActions); `fn` is false if cancelled. What
   *  follows is decided by afterAction (fast poll / refresh once / nothing). */
  async function act(key: string, fn: () => Promise<boolean>, doneText: string) {
    setBusy(key);
    setError(null);
    setNotice(null);
    let outcome: "done" | "cancelled" | "refused";
    try {
      outcome = (await fn()) ? "done" : "cancelled";
      if (outcome === "done") setNotice(doneText);
    } catch (e) {
      setError(newCardError(describeError(e), e instanceof ApiError ? e.code : null));
      outcome = "refused";
    } finally {
      setBusy(null);
    }
    const then = afterAction(outcome);
    if (then === "fast_poll") onActed();
    else if (then === "refresh_once") onRefused();
  }

  const runNow = () => act("run", () => runNowAction(api, c), "Queued. This page checks every 15 s for the next 2 minutes.");
  const resume = () => act("resume", async () => (await resumeAction(api, c)) !== false, "Resumed.");

  const older = async () => {
    setBusy("older");
    setError(null);
    try {
      if (await onLoadOlder()) setExpanded(true);
      else setError(newCardError("New runs keep arriving. Try Load older again in a moment.", null));
    } catch (e) {
      setError(newCardError(describeError(e), e instanceof ApiError ? e.code : null));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby={`h-${c.id}`} className="rounded-xl border border-line bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`h-${c.id}`} className="text-lg font-semibold">{c.name}</h2>
          <p className="text-sm text-muted">{PROVIDER_LABEL[c.provider]} · {cadenceLabel(c.cadenceSecs)}</p>
        </div>
        <p className={`text-sm font-semibold ${toneClass(health.tone)}`}>
          {health.label}{c.running && <span className="font-normal text-muted"> · running now</span>}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Last success" value={relativeTime(c.lastSuccessAt, now)} />
        <Fact label="Last run" value={relativeTime(c.lastRunAt, now)} />
        <Fact label="Next run" value={nextRunText(c, now)} />
        <Fact label="Last 24 h" value={`${c.last24h.ok} ok · ${c.last24h.error} failed · ${c.last24h.skipped} skipped`} />
        <Fact label="Failures in a row" value={c.consecutiveFailures === 0 ? "None" : `${c.consecutiveFailures}${c.failingSince ? `, since ${relativeTime(c.failingSince, now)}` : ""}`} tone={c.consecutiveFailures > 0 ? "bad" : undefined} />
        <Fact label="Credentials" value={c.hasCredentials ? `Last rotated ${relativeTime(c.credLastRotatedAt, now)}` : "Not set"} tone={c.hasCredentials ? undefined : "bad"} />
        {c.lastErrorClass && (
          <div className="flex min-w-0 flex-col sm:col-span-2 lg:col-span-3">
            <dt className="text-muted">Last error</dt>
            <dd className="break-words text-bad">{ERROR_CLASS_TEXT[c.lastErrorClass]}{c.lastError ? `: ${c.lastError}` : ""}</dd>
          </div>
        )}
      </dl>

      {admin && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {c.status === "needs_attention" && (
            <button type="button" disabled={busy !== null} onClick={() => void resume()} className={btnPrimary}>
              {busy === "resume" ? "Resuming…" : "Resume"}
            </button>
          )}
          <button type="button" disabled={busy !== null || !c.enabled || c.status !== "active"} onClick={() => void runNow()} className={btn}>
            {busy === "run" ? "Queuing…" : "Run now"}
          </button>
          <Link to="/settings/connectors" className="touch-target inline-flex items-center px-2 text-sm font-medium text-brand underline">Connector settings</Link>
        </div>
      )}
      {notice && <p role="status" className="mt-3 text-sm text-ok">{notice}</p>}
      {shownError && <p role="alert" className="mt-3 text-sm text-bad">{shownError}</p>}

      <h3 className="mt-5 text-sm font-semibold">Recent runs</h3>
      {runs.length === 0 ? (
        <p className="mt-1 text-sm text-muted">No runs yet.</p>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <thead className="border-b border-line text-muted">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">Queued</th>
                <th scope="col" className="px-3 py-2 font-medium">Status</th>
                <th scope="col" className="px-3 py-2 font-medium">Trigger</th>
                <th scope="col" className="px-3 py-2 font-medium">Took</th>
                <th scope="col" className="px-3 py-2 font-medium">Counts</th>
                <th scope="col" className="px-3 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const s = RUN_STATUS_TEXT[r.status];
                return (
                  <tr key={r.id} className="border-b border-line last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-2">{fmtTime(r.queuedAt)}</td>
                    <td className={`px-3 py-2 font-medium ${toneClass(s.tone)}`}>{s.label}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted">{r.trigger === "manual" ? "Run now" : "Schedule"}</td>
                    <td className="whitespace-nowrap px-3 py-2">{durationText(r.durationMs)}</td>
                    <td className="px-3 py-2 text-muted">{countsText(r.counts)}</td>
                    <td className="min-w-[12rem] break-words px-3 py-2">
                      {r.errorClass && <span className="text-bad">{ERROR_CLASS_TEXT[r.errorClass]}{r.message ? ": " : ""}</span>}
                      {r.message ?? (r.errorClass ? "" : "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {runs.length > COLLAPSED_RUNS && (
          <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className={btn}>
            {expanded ? "Show fewer" : `Show all ${runs.length}`}
          </button>
        )}
        {history?.olderCursor && (expanded || runs.length <= COLLAPSED_RUNS) && (
          <button type="button" disabled={busy !== null} onClick={() => void older()} className={btn}>
            {busy === "older" ? "Loading…" : "Load older"}
          </button>
        )}
      </div>
    </section>
  );
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: "bad" }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-muted">{label}</dt>
      <dd className={tone === "bad" ? "text-bad" : undefined}>{value}</dd>
    </div>
  );
}
